import React from "react";
import { Breadcrumb, Button, Dropdown, Space, Typography } from "antd";
import { ArrowLeftOutlined, MoreOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";

const { Title } = Typography;

interface BreadcrumbItem {
    title: string;
    href?: string;
}

interface ActionButton {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    href?: string;
    type?: "primary" | "default";
    variant?: "outlined" | "solid";
}

interface MoreMenuItem {
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    danger?: boolean;
}

interface PageHeaderProps {
    title: string;
    titleIcon?: React.ReactNode;
    breadcrumbs: BreadcrumbItem[];
    actions?: ActionButton[];
    moreMenuItems?: MoreMenuItem[];
    onBack?: () => void;
    showBack?: boolean;
    children?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    titleIcon,
    breadcrumbs,
    actions = [],
    moreMenuItems = [],
    onBack,
    showBack = true,
    children,
}) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    };

    const moreMenu: MenuProps = {
        items: moreMenuItems.map((item) => ({
            key: item.key,
            label: item.label,
            icon: item.icon,
            onClick: item.onClick,
            danger: item.danger,
        })),
    };

    return (
        <div className="page-header">
            {/* Breadcrumb Row */}
            <div className="page-header-breadcrumb">
                {showBack && (
                    <Button 
                        type="text" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={handleBack}
                        className="back-button"
                    />
                )}
                <Breadcrumb
                    separator="/"
                    items={breadcrumbs.map((item) => ({
                        title: item.href ? (
                            <Link to={item.href}>{item.title}</Link>
                        ) : (
                            item.title
                        ),
                    }))}
                />
            </div>

            {/* Title Row */}
            <div className="page-header-title-row">
                <div className="page-header-title">
                    {titleIcon && <span className="page-title-icon">{titleIcon}</span>}
                    <Title level={3} className="page-title-text">{title}</Title>
                </div>

                {/* Actions */}
                {(actions.length > 0 || moreMenuItems.length > 0) && (
                    <div className="page-header-actions">
                        <Space size="small">
                            {actions.map((action, index) => (
                                action.href ? (
                                    <Link key={index} to={action.href}>
                                        <Button
                                            type={action.type || "default"}
                                            icon={action.icon}
                                            className={`action-btn-${action.variant || 'outlined'}`}
                                        >
                                            {action.label}
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button
                                        key={index}
                                        type={action.type || "default"}
                                        icon={action.icon}
                                        onClick={action.onClick}
                                        className={`action-btn-${action.variant || 'outlined'}`}
                                    >
                                        {action.label}
                                    </Button>
                                )
                            ))}
                            {moreMenuItems.length > 0 && (
                                <Dropdown menu={moreMenu} trigger={["click"]} placement="bottomRight">
                                    <Button 
                                        type="text" 
                                        icon={<MoreOutlined />}
                                        className="more-actions-btn"
                                    />
                                </Dropdown>
                            )}
                        </Space>
                    </div>
                )}
            </div>

            {/* Optional children (e.g., stat cards specific to page) */}
            {children}
        </div>
    );
};
